/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
/* eslint-disable jsx-a11y/media-has-caption */
import { Pause, Play } from '@carbon/react/icons';
import { Button, Loading, TextArea, Tile } from 'carbon-components-react';
import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from '@ibm-watson/shared/store';
import { selectIsGenesysAudioConnectorInCpd } from '../../../../ia/store/features/selectors';
import * as speechApi from '../../../api/speech/index';
import { getDefaultLanguageText } from '../../../helpers/text-helpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectCurrentTextToSpeechInstance,
  selectTextToSpeechInstanceLoaded,
  selectTextToSpeechVoice
} from '../../../store/voice/selectors';
import {
  DEFAULT_VOICE_PITCH,
  DEFAULT_VOICE_SPEED,
  MAX_TEXT_LENGTH
} from '../constants';
import { convertAudioBlobToVisualizationData } from '../phone-helpers';
import { FIELDS } from '../schema';
import { AudioWave } from './AudioWave';
import { TextToSpeech as messages } from './messages';
import { TextToSpeechVoiceTile } from './TextToSpeechVoiceTile';

export const AudioVisualizer = () => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);

  // audio elements
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(null);
  const [audioPaused, setAudioPaused] = useState(false);
  const [audioCtx] = useState(new AudioContext());
  const [audioData, setAudioData] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioError, setAudioError] = useState(false);
  const [isParamsUpdated, setIsParamsUpdated] = useState(false);
  const audioRef = useRef(null);
  const isloadingCancelledAudioRef = useRef(false);
  const audioWaveContainerRef = useRef(null);

  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  const ttsVoiceData = useAppSelector(
    state => selectTextToSpeechVoice(state, getFieldValue(FIELDS.TTS.VOICE)),
    shallowEqual
  );

  const isTtsLoaded = useAppSelector(
    state => selectTextToSpeechInstanceLoaded(state),
    shallowEqual
  );

  const ttsVoiceSpeed = getFieldValue(FIELDS.TTS.RATE_PERCENTAGE);
  const ttsVoicePitch = getFieldValue(FIELDS.TTS.PITCH_PERCENTAGE);
  const ttsVoiceIdValue = getFieldValue(FIELDS.TTS.VOICE);
  const instanceId = getFieldValue(FIELDS.TTS.INSTANCE_ID) ?? '';

  // audio settings
  const [voiceSpeed, setVoiceSpeed] = useState(
    ttsVoiceSpeed ?? DEFAULT_VOICE_SPEED.toString()
  );
  const [voicePitch, setVoicePitch] = useState(
    ttsVoicePitch ?? DEFAULT_VOICE_PITCH.toString()
  );

  const [selectedLanguage, setSelectedLanguage] = useState(
    ttsVoiceIdValue && ttsVoiceIdValue.length > 2
      ? ttsVoiceIdValue.substr(0, 2)
      : ''
  );

  const [textAreaValue, setTextAreaValue] = useState(
    t(messages.visualizerTextAreaPlaceHolder)
  );

  const currentTextToSpeechInstance = useAppSelector(
    state => selectCurrentTextToSpeechInstance(state),
    shallowEqual
  );

  useEffect(() => {
    if (ttsVoiceIdValue && ttsVoiceIdValue.length > 2) {
      let newVoiceLanguage = ttsVoiceIdValue.substr(0, 2);
      newVoiceLanguage =
        newVoiceLanguage === 'zh'
          ? ttsVoiceIdValue.substr(0, 5)
          : newVoiceLanguage;
      if (newVoiceLanguage !== selectedLanguage) {
        const defaultLanguageText = getDefaultLanguageText(newVoiceLanguage);
        setTextAreaValue(defaultLanguageText);
      }
      setSelectedLanguage(newVoiceLanguage);
    }
  }, [selectedLanguage, ttsVoiceIdValue]);

  useEffect(() => {
    return () => {
      var audioElement = document.getElementById(
        'TextToSpeech__audio'
      ) as HTMLAudioElement;
      if (audioElement) {
        isloadingCancelledAudioRef.current = true;
        audioElement.pause();
        audioElement.src = '';
        audioElement.load();
      }
    };
  }, []);

  useEffect(() => {
    setVoiceSpeed(ttsVoiceSpeed);
  }, [ttsVoiceSpeed]);

  useEffect(() => {
    setVoicePitch(ttsVoicePitch);
  }, [ttsVoicePitch]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    if (
      navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1
    ) {
      audioRef.current.preload = 'metadata';
    }

    audioRef.current.addEventListener('play', () => {
      if (navigator.userAgent.indexOf('Safari') === -1) {
        setAudioLoading(false);
        setAudioPaused(false);
        setAudioPlaying(true);
      }
    });

    audioRef.current.addEventListener('playing', () => {
      if (navigator.userAgent.indexOf('Safari') !== -1) {
        setAudioLoading(false);
        setAudioPaused(false);
        setAudioPlaying(true);
      }
    });

    audioRef.current.addEventListener('loadeddata', () => {
      setAudioLoading(false);
      setAudioPaused(false);
      setAudioPlaying(false);
      setAudioError(false);
    });

    audioRef.current.addEventListener('ended', () => {
      setAudioPlaying(false);
    });

    audioRef.current.addEventListener('ended', () => {
      setAudioPlaying(false);
    });

    audioRef.current.addEventListener('error', () => {
      if (!isloadingCancelledAudioRef.current) {
        setAudioError(true);
      }
    });

    audioRef.current.addEventListener('loadedmetadata', () => {
      if (navigator.userAgent.indexOf('Safari') !== -1) {
        // safari works best when playing here
        if (audioRef.current && !audioRef.current.autoplay) {
          setAudioLoading(false);
        }
      }
    });
  }, [isTtsLoaded]);

  const buildReqUrl = async (autoplay: boolean) => {
    if (!audioRef.current || !ttsVoiceData || !currentTextToSpeechInstance) {
      return;
    }
    try {
      setAudioLoading(true);
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');

      const text = textAreaValue.replace(/[\r\n]/gm, ' ');
      const pitch = (voicePitch || DEFAULT_VOICE_PITCH).toString() + '%';
      const speed = (voiceSpeed || DEFAULT_VOICE_SPEED).toString() + '%';

      const audioBlob = isGenesysAudioConnectorEnabledInCpd
        ? await speechApi.synthesizeTextToSpeechInstanceInCpd({
            pitch_percentage: pitch,
            rate_percentage: speed,
            voice: ttsVoiceData.name,
            text: text,
            instanceId: instanceId
          })
        : await speechApi.synthesizeTextToSpeechInstance(
            currentTextToSpeechInstance.id,
            pitch,
            speed,
            text,
            ttsVoiceData.name
          );

      const audioUrl = URL.createObjectURL(audioBlob);

      const { duration, reducedFloatArray } =
        await convertAudioBlobToVisualizationData(audioBlob, audioCtx);
      if (!isloadingCancelledAudioRef.current) {
        setAudioData(reducedFloatArray);
        setAudioDuration(duration);
        if (audioRef.current) {
          audioRef.current.setAttribute('src', audioUrl);
          audioRef.current.setAttribute('type', 'audio/mp3');
          audioRef.current.controls = false;

          if (autoplay) {
            audioRef.current.autoplay = true;
          }
        }
      }
    } catch (e) {
      console.error(e);
      if (!isloadingCancelledAudioRef.current) {
        setAudioLoading(false);
      }
    }
  };

  // direct GET request for streaming audio from TTS
  const playAudioDirect = () => {
    if (!ttsVoiceData || !textAreaValue) {
      return;
    }
    if (isParamsUpdated) {
      if (audioRef.current) {
        setAudioPaused(false);
        setAudioPlaying(false);
      }
      setIsParamsUpdated(false);
      buildReqUrl(true);
      return;
    }
    if (!audioLoading) {
      if (audioPlaying && !audioPaused) {
        audioRef.current.pause();
        setAudioPaused(true);
        return;
      }
      if (audioPaused) {
        audioRef.current.play();
        setAudioPaused(false);
        return;
      }
      if (!audioPlaying) {
        if (
          navigator.userAgent.indexOf('Safari') !== -1 &&
          navigator.userAgent.indexOf('Chrome') === -1
        ) {
          buildReqUrl(true);
        } else {
          audioRef.current.currentTime = 0;
          audioRef.current.play();
        }
      }
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setAudioPlaying(false);
    setAudioError(false);
    setIsParamsUpdated(true);
  }, [currentTextToSpeechInstance]);

  useEffect(() => {
    setAudioError(false);
    if (!audioRef.current) {
      return;
    }
    if (ttsVoiceData) {
      if (!audioRef.current.src && !audioLoading) {
        buildReqUrl(false);
        setIsParamsUpdated(false);
        return;
      } else if (audioRef.current.src) {
        setIsParamsUpdated(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    textAreaValue,
    ttsVoiceData,
    ttsVoicePitch,
    ttsVoiceSpeed,
    ttsVoiceIdValue
  ]);

  // conditionally render a different icon depending on app state
  const renderAudioIcon = () => {
    if (audioLoading) {
      return <Loading small withOverlay={false} />;
    }
    if (audioPlaying && !audioPaused) {
      return <Pause size={20} />;
    }
    return <Play size={20} />;
  };

  if (audioError) {
    return (
      <Tile className="TextToSpeech__tile-skeleton">
        <div className="TextToSpeech__loading">
          <FormattedMessage {...messages.ttsAudioError} />
        </div>
      </Tile>
    );
  }
  return (
    <>
      <audio id="TextToSpeech__audio" ref={audioRef} />
      {isTtsLoaded ? (
        <Tile className="TextToSpeech__tile">
          {ttsVoiceData && <TextToSpeechVoiceTile voiceModel={ttsVoiceData} />}
          <div className="TextToSpeech__visualizer-container">
            <Button
              id="btn"
              className="TextToSpeech__visualizer-button"
              disabled={audioLoading}
              aria-label={t(messages.playAudioButtonAriaLabel)}
              onClick={() => playAudioDirect()}
              size="field">
              {renderAudioIcon()}
            </Button>
            {audioRef.current && (
              <AudioWave
                audioRef={audioRef}
                audioWaveContainerRef={audioWaveContainerRef}
                data={audioData}
                duration={audioDuration}
              />
            )}
          </div>
          <TextArea
            id="tts-text"
            className="TextToSpeech__visualizer-text-area"
            labelText={t(messages.visualizerTextAreaLabel)}
            light
            maxLength={MAX_TEXT_LENGTH}
            onChange={e => setTextAreaValue(e.target.value)}
            placeholder={t(messages.visualizerTextAreaPlaceHolder)}
            value={textAreaValue}
          />
        </Tile>
      ) : (
        <Tile className="TextToSpeech__tile-skeleton">
          <Loading className="TextToSpeech__loading" withOverlay={false} />
        </Tile>
      )}
    </>
  );
};
